"use client";

import { Button } from "@workspace/ui/components/button";
import { cn } from "@workspace/ui/lib/utils";
import { Loader2Icon } from "lucide-react";

/** Live-call lifecycle, mirroring the SDK's `LiveCallPhase`. */
export type ComposerLiveCallPhase =
  | "idle"
  | "connecting"
  | "listening"
  | "user-speaking"
  | "thinking"
  | "speaking"
  | "delegating"
  | "switching"
  | "errored";

/**
 * The composer's live-call seam (plan 042). Absent on `RichComposerProps` →
 * no call button. `useMagnusConversation().chatProps.liveCall` (or
 * `useMagnusLiveCall()`) returns a structurally matching source.
 */
export interface ComposerLiveCallSource {
  error: string | null;
  phase: ComposerLiveCallPhase;
  start(): unknown;
  stop(): void;
}

/** Whether the call button takes Send's place: only before a call (or
 *  while one connects), so the composer's Send never ends a call. */
export function showsLiveCallButton(liveCall: ComposerLiveCallSource): boolean {
  return (
    liveCall.phase === "idle" ||
    liveCall.phase === "errored" ||
    liveCall.phase === "connecting"
  );
}

function WaveformIcon() {
  return (
    <svg
      aria-hidden="true"
      className="size-4"
      fill="currentColor"
      viewBox="0 0 21 21"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect height="6" rx="0.5" width="1" x="0" y="7.5" />
      <rect height="10" rx="0.5" width="1" x="4" y="5.5" />
      <rect height="16" rx="0.5" width="1" x="8" y="2.5" />
      <rect height="10" rx="0.5" width="1" x="12" y="5.5" />
      <rect height="16" rx="0.5" width="1" x="16" y="2.5" />
      <rect height="6" rx="0.5" width="1" x="20" y="7.5" />
    </svg>
  );
}

/** Start-call button for the composer's right cluster, in Send's slot
 *  while there is nothing to send. Clicking while connecting cancels. */
export function ComposerLiveCallButton({
  liveCall,
  disabled,
}: {
  liveCall: ComposerLiveCallSource;
  disabled?: boolean;
}) {
  const connecting = liveCall.phase === "connecting";
  const label = connecting ? "Cancel voice call" : "Start voice call";
  return (
    <Button
      aria-busy={connecting}
      aria-label={label}
      // `composer-action` because whether send is the accent is a per-theme
      // answer, and this shares a slot with Submit — a slot that changed
      // colour as you typed would read as a different control. `rounded-lg`
      // tracks the base rather than clamping the way the size would, the
      // whole cluster sitting at the base radius
      // (`components/__tests__/dense-control-radius.test.tsx`).
      className={cn(
        "rounded-lg bg-composer-action text-composer-action-foreground",
        "hover:bg-composer-action/90"
      )}
      disabled={disabled}
      onClick={() => {
        if (connecting) {
          liveCall.stop();
        } else {
          Promise.resolve(liveCall.start()).catch(() => undefined);
        }
      }}
      size="icon-sm"
      title={connecting ? "Cancel" : "Start voice call"}
      type="button"
    >
      {connecting ? (
        <Loader2Icon className="size-3.5 animate-spin motion-reduce:animate-none" />
      ) : (
        <WaveformIcon />
      )}
    </Button>
  );
}
